<template>
  <button
    data-interactive="true"
    :type="type"
    ref="buttonRef"
    class="button-wrapper"
    :class="{
      'size-xs': size === 'xs',
      'size-s': size === 's',
      'size-m': size === 'm',
      'variant-primary': variant === 'primary',
      'variant-secondary': variant === 'secondary',
      'variant-tertiary': variant === 'tertiary',
      'only-icon': icon && !label && !iconRight,
      'left-icon': icon,
      'right-icon': iconRight,
      'focus-enabled': !whiteLabel,
      'no-padding': noPadding,
      variableWidth: variableWidth,
      disabled: disabled,
      active: active,
      block: block
    }"
    @click.prevent="e => handleClick(e)"
    :disabled="disabled"
  >
    <div v-if="loading" class="loading-container">
      <n-loading :dark="variant !== 'primary'" />
    </div>
    <div :class="['button-body', { loading }]">
      <n-icon v-if="icon" :size="iconSize" :icon="icon" :class="{ 'margin-right': !!label }" />
      <slot name="icon"></slot>
      <span v-if="label" class="span-wrapper" :class="{ 'margin-right': !!iconRight, 'icon-synergy': icon }">
        {{ label }}
      </span>
      <n-icon v-if="iconRight" :size="iconSize" :icon="iconRight" />
    </div>
  </button>
</template>

<script lang="ts">
import Team from '@/modules/team/models/Team'
import { ButtonShape } from '@/modules/team/models/WhiteLabelSettings'
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import { mapState } from 'pinia'
import NLoading from './NLoading.vue'
import { useTeamStore } from '@/modules/team/stores/TeamStore'

@Component({
  components: {
    NLoading
  },
  computed: {
    ...mapState(useTeamStore, ['currentTeam'])
  }
})
export default class NButton extends Vue {
  @Prop({ type: String, default: 'button' }) type?: String
  @Prop({
    type: String,
    validator: (prop: string) => ['primary', 'secondary', 'tertiary'].includes(prop),
    default: 'primary'
  })
  variant?: String
  @Prop({
    type: String,
    validator: (prop: string) => ['xs', 's', 'm'].includes(prop),
    default: 'm'
  })
  size?: String
  @Prop({
    type: String,
    validator: (prop: string) => ['s', 'm'].includes(prop),
    default: 's'
  })
  iconSize!: String
  @Prop({ type: String, default: '' }) label!: String
  @Prop({ type: String, default: '' }) icon!: String
  @Prop({ type: String, default: '' }) iconRight!: String
  @Prop({ type: Boolean, default: false }) disabled!: boolean
  @Prop({ type: Boolean, default: false }) loading!: boolean
  @Prop({ type: Boolean, default: false }) block!: boolean
  @Prop({ type: Boolean, default: false }) active!: boolean
  @Prop({ type: Boolean, default: false }) variableWidth!: boolean
  @Prop({ type: Boolean, default: false }) whiteLabel!: boolean
  @Prop({ type: Boolean, default: false }) noPadding!: boolean

  private currentTeam!: Team

  private roundedRectangleRadius = '12px'

  @Ref('buttonRef') buttonRef!: HTMLElement

  mounted() {
    if (this.whiteLabel) {
      const settings = this.currentTeam.whiteLabelSettings
      if (settings?.buttonShape === ButtonShape.RECTANGLE) {
        this.buttonRef.style.borderRadius = '0'
      } else if (settings?.buttonShape === ButtonShape.ROUNDED_RECTANGLE) {
        this.buttonRef.style.borderRadius = this.roundedRectangleRadius
      }
      if (this.variant === 'primary' && settings?.primaryColor) {
        this.buttonRef.style.backgroundColor = settings?.primaryColor
      }
    }
  }

  handleClick(event: Event) {
    if (this.disabled || this.loading) return
    this.$emit('click', event)
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.button-wrapper {
  position: relative;
  width: fit-content;
  border-radius: $notch-radius-full;
  border: 2px solid transparent;
  box-sizing: border-box;
  cursor: pointer;

  // `cursor: pointer` was unconditional, so a disabled button still invited a click it would
  // never handle. The per-variant `&.disabled` blocks below only change colours.
  &.disabled {
    cursor: not-allowed;
  }

  &.variableWidth {
    width: 100%;
  }

  &.size-m {
    @include notch-text-label-strong-M;
    padding: $notch-space-2x $notch-space-4x;

    .margin-right {
      margin-right: $notch-space-2x;
    }

    .loading-container {
      height: $notch-space-4_5x;
    }

    &.left-icon {
      padding-left: $notch-space-3x;
    }

    &.right-icon {
      padding-right: $notch-space-3x;
    }

    &.only-icon {
      // Special case
      padding: 0.6875rem;

      .loading-container {
        height: $notch-space-4x;
      }
    }

    &.no-padding {
      padding: 0;
    }
  }

  &.size-s {
    @include notch-text-label-strong-XS;
    padding: 0 $notch-space-3x;
    height: $notch-space-8x;
    display: flex;
    align-items: center;
    justify-content: center;

    .margin-right {
      margin-right: $notch-space-2x;
    }

    .loading-container {
      height: $notch-space-4x;
    }

    &.left-icon {
      padding-left: $notch-space-2x;
    }

    &.right-icon {
      padding-right: $notch-space-2x;
    }

    &.only-icon {
      padding: $notch-space-2x;
      border: none;
    }

    &.no-padding {
      padding: 0;
    }
  }

  &.size-xs {
    @include notch-text-label-strong-XS;
    padding: $notch-space-1x $notch-space-2x;
    height: $notch-space-6x;
    display: flex;
    align-items: center;
    justify-content: center;

    .margin-right {
      margin-right: $notch-space-2x;
    }

    .loading-container {
      height: $notch-space-4x;
    }

    &.left-icon {
      padding-left: $notch-space-2x;
    }

    &.right-icon {
      padding-right: $notch-space-2x;
    }

    &.only-icon {
      padding: $notch-space-2x;
      border: none;
    }

    &.no-padding {
      padding: 0;
    }
  }
}

.button-body {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .span-wrapper {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.loading {
    visibility: hidden;
    height: 0;
  }
}

.variant-primary {
  background-color: $notch-color-neutral-grey-900;
  color: $notch-color-neutral-grey-000;

  &:hover:enabled {
    background-color: $notch-color-neutral-grey-800;
  }

  &.focus-enabled:focus:enabled {
    @include notch-style-focus;
  }

  &.disabled {
    background-color: $notch-color-neutral-sand-400;
    color: $notch-color-neutral-sand-100;
  }
}

.variant-secondary {
  background-color: $notch-color-neutral-grey-000;
  color: $notch-color-neutral-grey-900;
  border: 2px solid $notch-color-neutral-sand-200;

  &:hover {
    background-color: $notch-color-neutral-sand-100;
  }

  &:focus:enabled {
    @include notch-style-focus;
  }

  &.active:enabled {
    background-color: $notch-color-neutral-sand-200;
  }

  &.disabled {
    color: $notch-color-neutral-sand-500;
    border-color: $notch-color-neutral-sand-100;
  }
}

.variant-tertiary {
  background-color: $notch-color-neutral-grey-000;
  color: $notch-color-neutral-grey-900;

  &:hover:enabled {
    background-color: $notch-color-neutral-sand-100;
  }

  &:focus:enabled {
    @include notch-style-focus;
  }

  &.active {
    background-color: $notch-color-neutral-sand-200;
  }

  &.disabled {
    color: $notch-color-neutral-sand-500;
  }
}

.block {
  width: 100%;

  .button-body {
    justify-content: center;
  }
}

.span-wrapper {
  @include mobile {
    width: 100%;
    overflow: hidden;
  }

  @include tablet {
    width: 100%;
    overflow: hidden;
  }
}

.icon-synergy {
  @include mobile {
    width: auto;
  }
}
</style>
